export interface AssignContact {
  id: number;
  first_name: string;
  last_name: string;
  profile_image: string;
  by: "Employee" | "Company" | "Self Employee";
  role?: string;
}

export const mapAssignContact = (data: any): AssignContact => {
  if (!data) {
    throw new Error("Invalid AssignContact type");
  }

  return {
    id: Number(data.id),
    first_name: data.first_name ?? "",
    last_name: data.last_name ?? "",
    profile_image: data.profile_image ?? "",
    by: data.by,
    role: data.role ?? data.role_name ?? "",
  };
};

export const mapAssignContacts = (data: any[]): AssignContact[] => {
  if (!Array.isArray(data)) return [];
  return data.map(mapAssignContact);
};

export interface AssignBy {
  first_name: string;
  last_name: string;
  profile_image: string;
}

export const mapAssignBy = (data: any): AssignBy => {
  if (!data) {
    throw new Error("Invalid AssignBy type");
  }

  return {
    first_name: data.first_name ?? "",
    last_name: data.last_name ?? "",
    profile_image: data.profile_image ?? "",
  };
};

export interface TaskProofData {
  id?: number;
  task_id?: number;
  note?: string;
  file_path?: string;
  created_at?: string;
}

export const mapTaskProofData = (data: any): TaskProofData => {
  return {
    id: data?.id,
    task_id: data?.task_id,
    note: data?.note ?? "",
    file_path: data?.file_path ?? "",
    created_at: data?.created_at ?? "",
  };
};

export const mapTaskProofDataList = (data: any[]): TaskProofData[] => {
  if (!Array.isArray(data)) return [];
  return data.map(mapTaskProofData);
};

export interface TaskFollowUp {
  id?: number;
  task_id?: number;
  task_date?: string;
  followup_date?: string;
  note?: string;
  created_at?: string;
}

export const mapTaskFollowUp = (data: any): TaskFollowUp => {
  return {
    id: data?.id,
    task_id: data?.task_id,
    task_date: data?.task_date ?? "",
    followup_date: data?.followup_date ?? data?.follow_up_date ?? "",
    note: data?.note ?? "",
    created_at: data?.created_at ?? "",
  };
};

export const mapTaskFollowUps = (data: any[]): TaskFollowUp[] => {
  if (!Array.isArray(data)) return [];
  return data.map(mapTaskFollowUp);
};

export interface TaskCarryForwardReason {
  id: number;
  task_carry_forward_reason: string;
  order_no?: number;
  is_default_approved?: number;
  status?: number;
}

export const mapTaskCarryForwardReason = (
  data: any
): TaskCarryForwardReason => {
  return {
    id: Number(data?.id),
    task_carry_forward_reason:
      data?.task_carry_forward_reason ?? data?.reason ?? data?.name ?? "",
    order_no: data?.order_no,
    is_default_approved: data?.is_default_approved,
    status: data?.status,
  };
};

export const mapTaskCarryForwardReasons = (
  data: any[]
): TaskCarryForwardReason[] => {
  if (!Array.isArray(data)) return [];
  return data.map(mapTaskCarryForwardReason);
};

export interface TaskCarryForward {
  id?: number;
  task_id?: number;
  carry_forward_id?: number;
  task_date?: string;
  carry_forward_date?: string;
  is_default_approved?: number;
  is_approved?: number;
  note?: string;
  created_at?: string;
  forward_reason?: TaskCarryForwardReason | null;
}

export const mapTaskCarryForward = (data: any): TaskCarryForward => {
  return {
    id: data?.id,
    task_id: data?.task_id,
    carry_forward_id: data?.carry_forward_id,
    task_date: data?.task_date ?? data?.taskDate ?? "",
    carry_forward_date:
      data?.carry_forward_date ?? data?.carryForwardDate ?? "",
    is_default_approved: data?.is_default_approved ?? 0,
    is_approved: data?.is_approved ?? 0,
    note: data?.note ?? "",
    created_at: data?.created_at ?? "",
    forward_reason: data?.forward_reason
      ? mapTaskCarryForwardReason(data.forward_reason)
      : data?.reason
      ? mapTaskCarryForwardReason(data.reason)
      : null,
  };
};

export const mapTaskCarryForwards = (data: any[]): TaskCarryForward[] => {
  if (!Array.isArray(data)) return [];
  return data.map(mapTaskCarryForward);
};

export interface Tasks {
  task_id: number;
  project_reference: string;
  task_name: string;
  hours?: number;
  minute?: number;
  description?: string;
  deu_date: string;
  task_date: string;
  status?: string;
  priority: "High" | "Medium" | "Low";
  is_completed: number;
  assign: AssignContact[];
  assign_by?: AssignBy | null;
  proof_data: TaskProofData[];
  followup_data: TaskFollowUp[];
  carry_forward_data: TaskCarryForward[];
}

export const mapTask = (data: any): Tasks => {
  if (!data) {
    throw new Error("Invalid Task type");
  }

  return {
    task_id: Number(data.task_id),
    project_reference: data.project_reference ?? "",
    task_name: data.task_name ?? "",
    hours: Number(data.hours ?? 0),
    minute: Number(data.minute ?? 0),
    description: data.description ?? "",
    deu_date: data.deu_date ?? "",
    task_date: data.task_date ?? "",
    status: data.status ?? "",
    priority: data.priority ?? "Low",
    is_completed: Number(data.is_completed ?? 0),
    assign: mapAssignContacts(data.assign ?? []),
    assign_by: data.assign_by != null ? mapAssignBy(data.assign_by) : null,

    proof_data: mapTaskProofDataList(data.proof_data ?? []),
    followup_data: mapTaskFollowUps(data.followup_data ?? []),
    carry_forward_data: mapTaskCarryForwards(data.carry_forward_data ?? []),
  };
};

export const mapTasks = (data: any[]): Tasks[] => {
  if (!Array.isArray(data)) return [];
  return data.map(mapTask);
};

export interface Assign {
  id: number;
  by: "Employee" | "Company" | "Self Employee";
}

export interface CreateTaskArray {
  created_token: string;
  task_name: string;
  description?: string;
  deu_date: string;
  hours?: number | 0;
  minute?: number | 0;
  priority?: string;
  assign: AssignContact[];
}

export interface Projects {
  project_id: number;
  job_number: number;
  project_type: "CP" | "DP";
  address: string;
}